import { useCallback, useEffect, useState } from 'react';
import { subscribeToPush, unsubscribeFromPush, useVapidPublicKey } from './api';

/**
 * Web Push, from the browser's side.
 *
 * The whole point of this file is that the phone buzzes when a request is
 * waiting on someone. A 60-second poll cannot do that: the tab is closed, the
 * screen is off, and the approval sits until whoever it is happens to open the
 * app. Push is the only mechanism that reaches a locked phone.
 *
 * The awkward part is iOS. Safari has supported Web Push since 16.4, but ONLY
 * for a site that has been added to the Home Screen — in a normal Safari tab
 * `PushManager` exists and `Notification.requestPermission()` throws. So the
 * state machine has a rung the other platforms do not: "supported, but you have
 * to install it first", with instructions rather than a button that fails.
 */

export type PushState =
  | 'unsupported' // no service worker / no PushManager at all
  | 'needs-install' // iOS Safari in a tab: works once added to the Home Screen
  | 'unconfigured' // server has no VAPID key pair — push is off for everyone
  | 'default' // supported and available, permission not yet asked
  | 'denied' // the user said no; only the OS settings can undo it
  | 'subscribed'; // buzzing

const SW_URL = '/sw.js';

export function isIos(): boolean {
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports itself as a Mac; the touch points give it away.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari's own, non-standard flag — still the only one iOS sets.
    (navigator as { standalone?: boolean }).standalone === true
  );
}

export function isPushCapable(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Registers the worker that receives the pushes.
 *
 * Deliberately not awaited by anything that renders: a failed registration must
 * not keep the app from loading. Without a worker the app is exactly what it
 * was before — polling, in-app badges — which is a degradation, not a fault.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(SW_URL, { scope: '/' });
  } catch {
    return null;
  }
}

/** VAPID keys travel as base64url; `applicationServerKey` wants raw bytes. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalised);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

/** Once per page load, not once per component mount — the hook is used on more
 *  than one screen and this must not become a request per navigation. */
let resynced = false;

function encodeKey(subscription: PushSubscription, name: 'p256dh' | 'auth'): string {
  const key = subscription.getKey(name);
  if (!key) throw new Error('Abonelik anahtarı okunamadı.');
  return window.btoa(String.fromCharCode(...new Uint8Array(key)));
}

/**
 * Drives the permission → subscribe → tell-the-server sequence, and reports
 * where it currently stands so the UI can say something true.
 */
export function usePushNotifications() {
  const vapid = useVapidPublicKey();
  const [state, setState] = useState<PushState>('unsupported');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const publicKey = vapid.data?.publicKey ?? null;

  const refresh = useCallback(async () => {
    if (!isPushCapable()) {
      setState(isIos() && !isStandalone() ? 'needs-install' : 'unsupported');
      return;
    }
    if (isIos() && !isStandalone()) {
      setState('needs-install');
      return;
    }
    if (vapid.isSuccess && !publicKey) {
      setState('unconfigured');
      return;
    }
    if (Notification.permission === 'denied') {
      setState('denied');
      return;
    }
    const registration = await navigator.serviceWorker.getRegistration();
    const existing = await registration?.pushManager.getSubscription();
    setState(existing ? 'subscribed' : 'default');

    // Repairs a rotation the worker could not report, and a row lost to a
    // database restore: the browser is the authority on what its endpoint is,
    // so re-assert it once per load. The server upserts, so this is free.
    if (existing && !resynced) {
      resynced = true;
      try {
        await subscribeToPush({
          endpoint: existing.endpoint,
          p256dh: encodeKey(existing, 'p256dh'),
          auth: encodeKey(existing, 'auth'),
          userAgent: navigator.userAgent.slice(0, 200),
        });
      } catch {
        /* the badge and the poll still work; this is the phone-buzz layer */
      }
    }
  }, [publicKey, vapid.isSuccess]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    setError(null);
    if (!publicKey) {
      setError('Sunucuda push anahtarları tanımlı değil.');
      return;
    }
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'denied' : 'default');
        return;
      }

      // `register` is idempotent; calling it here means the permission flow
      // works even on a first visit where the background registration lost the
      // race.
      const registration =
        (await navigator.serviceWorker.getRegistration()) ?? (await registerServiceWorker());
      if (!registration) throw new Error('Service worker kaydedilemedi.');
      await navigator.serviceWorker.ready;

      const existing = await registration.pushManager.getSubscription();
      // A stale subscription made with a previous key pair will never decrypt;
      // drop it rather than sending pushes into a black hole.
      if (existing) await existing.unsubscribe();

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      });

      await subscribeToPush({
        endpoint: subscription.endpoint,
        p256dh: encodeKey(subscription, 'p256dh'),
        auth: encodeKey(subscription, 'auth'),
        userAgent: navigator.userAgent.slice(0, 200),
      });
      setState('subscribed');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bildirim izni alınamadı.');
      await refresh();
    } finally {
      setBusy(false);
    }
  }, [publicKey, refresh]);

  const disable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        // Server first: if the browser unsubscribes and the round trip then
        // fails, the row lingers and the server keeps pushing to an endpoint
        // that 410s forever.
        await unsubscribeFromPush(subscription.endpoint).catch(() => undefined);
        await subscription.unsubscribe();
      }
      setState('default');
    } finally {
      setBusy(false);
    }
  }, []);

  return { state, busy, error, enable, disable, refresh };
}
